import { purposeMap } from "@/features/assessment-v2/purposeMap";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { TraitStrengths } from "@/components/report/TraitStrengths";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, ChevronDown, ChevronUp, Download, History, Info, RotateCcw, Star } from "lucide-react";
import { AssessmentV2Provider, useAssessmentV2 } from "@/features/assessment-v2/store";
import { useEffectiveRunState } from "@/features/assessment-v2/useEffectiveRunState";
import {
  ARCHETYPE_TAGLINES, DISTANCE_STYLES, backgroundBlock, composeHeadline, composeLearner,
  formatSalary, mobilityResultNote, topArchetypes, whyItMatched,
  causeSteps, explainContext, lightsYouUpNote,
  type CauseStepBlock, type MatchExplainContext,
} from "@/features/assessment-v2/results";
import { loadCareersBundle } from "@/data/careersBundle";
import { bundleToCareers } from "@/lib/careersToScoring";
import { loadMarketIntelligence } from "@/lib/marketIntelligence";
import { matchCareers, type MatchResult, type Learner } from "@/lib/scoring";
import { scoreWorlds, pickPeekCareers, type WorldScore } from "@/features/assessment-v2/worlds";
import { parseBand } from "@/features/assessment-v2/band";
import { useSearchParams } from "react-router-dom";
import type { Archetype, ArchetypeVector } from "@/features/assessment-v2/archetypes";
import { emptyVector } from "@/features/assessment-v2/archetypes";
import { useCanonicalTakeover } from "@/features/assessment-v2/useCanonicalReport";
import { recordReconciliation } from "@/features/assessment-v2/persistence";
import {
  CanonicalReadyBanner, PendingWritesBanner, PipelineFailureBanner, SavedConfirmation,
} from "@/features/assessment-v2/components/CanonicalBanners";
import { CanonicalCareerList } from "@/features/assessment-v2/components/CanonicalCareerList";
import { DnaReveal } from "@/features/assessment-v2/components/DnaReveal";
import { writeLocalDna } from "@/features/assessment-v2/localDna";
import { captureDnaSnapshot } from "@/features/assessment-v2/snapshot";
import { recordRouteHit } from "@/lib/routeDiag";
import { YomaSays } from "@/features/assessment-v2/components/YomaSays";
import { buildConvergence } from "@/features/assessment-v2/convergence";
import { readApprovedObserverInput } from "@/features/assessment-v2/observerStore";
import { firstPersonFamilyStrength } from "@/features/assessment-v2/results";
import { ConvergenceVenn } from "@/features/assessment-v2/components/ConvergenceVenn";
import { IkigaiKid } from "@/features/assessment-v2/components/IkigaiKid";
import { LIKE_ITEMS } from "@/features/assessment-v2/screens/Part1ExplorerScreens";
import { cloudStorage } from "@/lib/cloudStore";
import OccupationStandardsNote from "@/components/OccupationStandardsNote";
import OnetAttribution from "@/components/OnetAttribution";




const SAVED_KEY = "wcc360.v2.saved_careers";

function useSaved(): [Set<string>, (id: string) => void] {
  const [saved, setSaved] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(cloudStorage.getItem(SAVED_KEY) ?? "[]")); }
    catch { return new Set(); }
  });
  const toggle = (id: string) => {
    setSaved((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { cloudStorage.setItem(SAVED_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };
  return [saved, toggle];
}

interface MatchState {
  learner: Learner;
  headline: string;
  primaryTag: string;
  secondaryTag: string;
  results: MatchResult[];
  eligiblePool: number;
  totalConsidered: number;
  ruledOutByStudy: number;
  studyRelaxed: boolean;
}

/**
 * 12–14 "Explorers" result.
 *
 * A twelve-year-old is never shown job titles as recommendations, never a
 * salary, never a match percentage, and never the words "best match". They
 * are shown three career WORLDS and their archetype — because "you're a
 * Builder" is a thing a twelve-year-old can carry for a decade, and
 * "Irrigation Engineer, 87% match" is not.
 */
function ExplorerResult() {
  const { state: localState, band, reset } = useAssessmentV2();
  const { state } = useEffectiveRunState(localState, band);
  const navigate = useNavigate();
  const [worlds, setWorlds] = useState<WorldScore[] | null>(null);
  const [revealSeen, setRevealSeen] = useState(false);

  const [archetype, setArchetype] = useState<{ primary: string; tag: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Explorers get the sync and failure surfaces, and nothing else from the
  // canonical takeover: this band is never shown ranked job titles, sealed
  // report or not.
  const takeover = useCanonicalTakeover(band);


  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [bundle, market] = await Promise.all([
          loadCareersBundle(),
          loadMarketIntelligence(),
        ]);
        const careers = bundleToCareers(bundle, (id) => market.get(id));
        const learner = composeLearner(state);
        const tally = (state.part2.archetype_tally as ArchetypeVector) ?? emptyVector();
        const { primary } = topArchetypes(tally);
        if (cancelled) return;
        setWorlds(scoreWorlds(learner, careers));
        setArchetype({ primary, tag: ARCHETYPE_TAGLINES[primary] });
        // The snapshot every other surface reads is written by the shared
        // capture, from this same state — so the dashboard, cards, report and
        // roadmap always show the run the child just finished.
        void captureDnaSnapshot(band, state);
      } catch (e: unknown) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Something didn't load.");
      }
    })();
    return () => { cancelled = true; };
  }, [state]);

  if (err) {
    return (
      <div className="min-h-dvh bg-background text-foreground grid place-items-center px-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="font-display text-2xl">That didn't load. Nothing you said is lost.</h1>
          <button
            onClick={() => navigate("/assessment/v2?band=12-14")}
            className="min-h-[48px] px-6 rounded-2xl bg-primary text-primary-foreground"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  if (!worlds || !archetype) {
    return (
      <div className="min-h-dvh bg-background text-foreground grid place-items-center px-6">
        <div className="text-center text-muted-foreground animate-pulse">
          Reading what you told us…
        </div>
      </div>
    );
  }

  const top3 = worlds.slice(0, 3);

  // Exactly four kinds of work, family-diverse, drawn from the worlds that
  // fit. Four is a deliberate cap: a twelve-year-old handed twelve job
  // titles reads it as a test result. Four reads as "go have a look".
  const peekCareers = pickPeekCareers(top3);

  const loveLabels = (state.part1.what_you_like ?? [])
    .map((id) => LIKE_ITEMS.find((i) => i.id === id)?.label.toLowerCase())
    .filter((v): v is string => Boolean(v));

  if (!revealSeen) {
    return (
      <DnaReveal
        cards={[
          {
            eyebrow: "Your Career DNA™",
            headline: "We read every answer you gave us.",
            body: "Here's what it says about you. Three cards, then your worlds.",
          },
          {
            eyebrow: "How you work",
            headline: `You're a ${archetype.primary}.`,
            body: `That means ${archetype.tag}.`,
          },
          {
            eyebrow: "What's next",
            headline: "Eight worlds of work. Three of them fit you.",
            body: "A world is a kind of work, not a job. You have years to pick a job.",
          },
        ]}
        ctaLabel="Show me my worlds"
        onDone={() => setRevealSeen(true)}
      />
    );
  }



  return (
    <div className="min-h-dvh bg-background text-foreground">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-background/70 border-b border-border">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.2em] text-primary">Your worlds</div>
          <div className="flex items-center gap-2">
            <Link
              to="/hub/you#history"
              className="min-h-[40px] px-3 rounded-xl border border-border bg-card hover:bg-secondary
                text-sm inline-flex items-center gap-2
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <History className="h-4 w-4" /> Saved attempts
            </Link>
            <button
              onClick={() => { reset(); navigate("/assessment/v2?band=12-14"); }}
              className="min-h-[40px] px-3 rounded-xl border border-border bg-card hover:bg-secondary
                text-sm inline-flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" /> Start again
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <h1 className="font-display text-3xl md:text-5xl leading-tight font-medium">
          You're a {archetype.primary}.
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          That means {archetype.tag}.
        </p>

        <YomaSays className="mt-6">
          I'm YoMA, and I'll be with you the whole way. We're not picking a job today — you
          have years for that. Today we're looking at what you care about, and how you could
          use it to help people.
        </YomaSays>

        {/* Ported v1 trait set — self-report, shown back for explainability. */}
        <TraitStrengths part2={state.part2} />

        <PendingWritesBanner count={takeover.pendingWrites} />
        <SavedConfirmation count={takeover.pendingWrites} completedAt={takeover.completedAt} />
        <PipelineFailureBanner takeover={takeover} />

        <div className="mt-12">
          <IkigaiKid
            loves={loveLabels}
            archetypePrimary={archetype.primary}
            archetypeTag={`${archetype.tag}.`}
            worldName={top3[0]?.world ?? "work"}
            worldBlurb={top3[0]?.blurb ?? ""}
          />
        </div>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-medium">Three worlds that fit you</h2>
          <p className="text-sm text-muted-foreground mt-1">
            A world is a kind of work, not a job. You have years to pick a job.
          </p>

          <div className="mt-5 space-y-4">
            {top3.map((w) => (
              <article key={w.world} className="rounded-3xl border border-border bg-card p-5 md:p-6">
                <h3 className="font-display text-xl md:text-2xl font-medium">{w.world}</h3>
                <p className="mt-2 text-foreground/90 leading-relaxed">{w.blurb}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-12" aria-labelledby="peek-heading">
          <h2 id="peek-heading" className="font-display text-2xl font-medium">
            Four kinds of work to peek at
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Not a plan. Not a match. Just four real jobs where people do the kind of thing
            you seem to like — so you can see what it actually looks like.
          </p>

          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {peekCareers.map((c) => (
              <li key={c.ciId}>
                <Link
                  to={`/careers/${encodeURIComponent(c.ciId)}`}
                  aria-label={`Explore what a ${c.title} actually does`}
                  className="group flex min-h-[88px] h-full flex-col justify-between rounded-3xl border border-border
                    bg-card p-5 transition-colors hover:border-primary/60 hover:bg-primary/5
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
                    focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <span className="font-display text-lg font-medium">{c.title}</span>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary">
                    See a day in this life
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <footer className="mt-14 pt-8 border-t border-border text-sm text-muted-foreground">
          We looked at 5,877 kinds of work and grouped them into eight worlds. Nothing here is
          decided, and nothing is closed off. Come back whenever you want — you're allowed to
          change your mind.
          <OccupationStandardsNote className="mt-4" />
          <OnetAttribution className="mt-4" />
        </footer>
      </main>
    </div>
  );
}


function ResultInner() {
  const { state: localState, band, reset } = useAssessmentV2();
  // Score the durable run when this device's copy is an empty shell.
  const { state } = useEffectiveRunState(localState, band);
  const navigate = useNavigate();
  const [m, setM] = useState<MatchState | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [familyFilter, setFamilyFilter] = useState<string | null>(null);
  const [saved, toggleSaved] = useSaved();
  const [revealSeen, setRevealSeen] = useState(false);

  // The sealed report, if and when it arrives. Until then this page is a
  // preview computed on this device.
  const takeover = useCanonicalTakeover(band);
  const reconciled = useRef(false);

  // Log where the on-device preview and the sealed report disagree. Silent
  // divergence between two engines is how wrong results survive.
  useEffect(() => {
    if (reconciled.current) return;
    if (!takeover.canonical || !takeover.sessionId || !m) return;
    reconciled.current = true;
    void recordReconciliation({
      band,
      sessionId: takeover.sessionId,
      clientTop10: m.results.slice(0, 10).map((r) => r.ciId),
      canonicalTop10: takeover.canonical.careers.slice(0, 10).map((c) => c.careerId),
      clientArchetype: m.learner.archetypePrimary ?? null,
      canonicalArchetype: takeover.canonical.archetypePrimary,
      details: {
        client_engine: "client-preview",
        report_hash: takeover.canonical.reportHash,
        eligible_pool: m.eligiblePool,
        study_relaxed: m.studyRelaxed,
      },
    });
  }, [takeover.canonical, takeover.sessionId, m, band]);



  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // One bulk market read per request, cached an hour, indexed by ci_id.
        const [bundle, market] = await Promise.all([
          loadCareersBundle(),
          loadMarketIntelligence(),
        ]);
        const careers = bundleToCareers(bundle, (id) => market.get(id));
        const totalConsidered = careers.length;


        const tally = (state.part2.archetype_tally as ArchetypeVector) ?? emptyVector();
        let learner = composeLearner(state);

        // First pass.
        let out = matchCareers(learner, careers, 15);
        let studyRelaxed = false;

        // Empty-state graceful degradation.
        if (out.results.length < 3 && learner.studyCapacity !== "degree") {
          const nextCap = learner.studyCapacity === "none" ? "short"
            : learner.studyCapacity === "short" ? "parttime" : "degree";
          learner = { ...learner, studyCapacity: nextCap };
          out = matchCareers(learner, careers, 15);
          studyRelaxed = true;
        }

        // The chips name the learner's composed archetypes (revealed choices
        // plus the bounded passion/dream/pull nudges), so their taglines must
        // come from the same pair — reading them off the raw tally printed
        // "Caregiver" above the Creator line.
        const primary = learner.archetypePrimary as Archetype;
        const secondary = learner.archetypeSecondary as Archetype;
        const headline = composeHeadline(learner, tally);


        // Count careers ruled out because they exceed capacity.
        const scoringMod = await import("@/lib/scoring");
        const cap = scoringMod.effectiveCapacity(learner);
        const ruled = careers.reduce((n, c) => (scoringMod.entryCost(c) > cap ? n + 1 : n), 0);

        if (cancelled) return;
        setM({
          learner,
          headline,
          primaryTag: ARCHETYPE_TAGLINES[primary],
          secondaryTag: ARCHETYPE_TAGLINES[secondary],
          results: out.results,
          eligiblePool: out.eligiblePool,
          totalConsidered,
          ruledOutByStudy: ruled,
          studyRelaxed,
        });

        // Hand the rest of the shell (YoMA, Career DNA™, Explorer cards)
        // something true to read the moment the run ends, instead of an
        // empty state that reads as "your assessment didn't count". The
        // sealed report still wins the moment it lands.
        writeLocalDna(band, {
          archetypePrimary: primary,
          archetypeSecondary: secondary,
          headline,
          matches: out.results.slice(0, 6).map((r) => ({
            id: r.ciId,
            name: r.title,
            cluster: r.family,
            tier: r.tier ?? null,
            explanation: null,
          })),
        });
      } catch (e: unknown) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Failed to load careers.");
      }
    })();
    return () => { cancelled = true; };
  }, [state]);

  const [order, setOrder] = useState<"pay" | "fit">("pay");
  const grouped = useMemo(() => {
    if (!m) return null;
    const results = familyFilter
      ? m.results.filter((r) => r.family === familyFilter)
      : m.results;
    const best: MatchResult[] = [];
    const also: MatchResult[] = [];
    const explore: MatchResult[] = [];
    for (const r of results) {
      if (r.tier === "Best match") best.push(r);
      else if (r.tier === "Also consider") also.push(r);
      else if (r.tier === "Explore") explore.push(r);
    }
    // Display only: top 3 then 3 stretch roles (further-out tier first).
    // "pay" reorders the fits by verified pay (unknown pay goes last); "fit" keeps engine order.
    const fits = [...best, ...also];
    const pay = (r: MatchResult) => r.salaryHi ?? r.salaryLo ?? -1;
    const pool = order === "pay" ? [...fits].sort((a, b) => pay(b) - pay(a)) : fits;
    const ranked = [...pool, ...explore];
    const top = ranked.slice(0, 3);
    const rest = ranked.slice(3);
    const stretch = [...rest.filter((r) => r.tier === "Explore"), ...rest.filter((r) => r.tier !== "Explore")].slice(0, 3);
    return { best, also, explore, top, stretch };
  }, [m, familyFilter, order]);

  // Convergence of Passions & Careers. Presentation only — it reads the
  // matches the engine already produced and never re-scores them. Declared
  // before any early return so the hook order never changes (React #310).
  const convergence = useMemo(() => (m ? buildConvergence({
    band,
    part1: state.part1,
    slots: state.part2.slots as never,
    learner: m.learner,
    results: m.results,
    eligiblePool: m.eligiblePool,
    ruledOutByStudy: m.ruledOutByStudy,
    studyRelaxed: m.studyRelaxed,
    observer: state.part1.observer_input ?? readApprovedObserverInput() ?? undefined,
    firstPersonStrength: firstPersonFamilyStrength(state, band),
  }) : null), [band, state, m]);



  if (err) {
    return (
      <div className="min-h-dvh bg-background text-foreground grid place-items-center px-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="font-display text-2xl">We couldn't load the careers just now.</h1>
          <p className="text-sm text-muted-foreground">
            Nothing you told us is lost. Go back and we'll pick up exactly where you left off —
            you never start from zero again.
          </p>
          <p className="text-xs text-muted-foreground/70">{err}</p>
          <button
            onClick={() => navigate("/assessment/v2")}
            className="min-h-[48px] px-6 rounded-2xl bg-primary text-primary-foreground"
          >
            Back to where you were
          </button>
        </div>
      </div>
    );
  }

  if (!m || !grouped) {
    return (
      <div className="min-h-dvh bg-background text-foreground grid place-items-center px-6">
        <div className="text-center space-y-2">
          <div className="text-muted-foreground animate-pulse">
            Matching what you told us to all 5,000+ careers of the future…
          </div>
          <div className="text-xs text-muted-foreground/70">This takes a moment. Worth it.</div>
        </div>
      </div>
    );
  }

  if (!revealSeen) {
    return (
      <DnaReveal
        cards={[
          // One opening card, not two: "we read your answers" and "we read the
          // careers" were saying the same thing twice, and "against you" read
          // like the corpus was being used to judge the learner.
          {
            eyebrow: "Your Career DNA™",
            headline: "We read every answer you gave us.",
            body: `Then we looked at all ${m.totalConsidered.toLocaleString("en-IN")} careers we hold — ${m.eligiblePool.toLocaleString("en-IN")} of them are open to you right now, and we'll tell you why each one fits.`,
          },
          {
            eyebrow: "How you work",
            headline: `You lead with ${m.learner.archetypePrimary}.`,
            body: `That means ${m.primaryTag}.`,
          },
          {
            eyebrow: "And underneath",
            headline: `You carry ${m.learner.archetypeSecondary} with it.`,
            body: `${m.secondaryTag}. Together, those two shape what fits you.`,
          },
        ]}

        ctaLabel="Show me where I'm heading"
        onDone={() => setRevealSeen(true)}
      />
    );
  }

  const explain = explainContext({ band, part1: state.part1, part2: state.part2 });
  const causeBlocks = causeSteps({ band, part1: state.part1, part2: state.part2 }, m.results);
  const purpose = purposeMap(state.part1, m.results);
  const lightsNote = lightsYouUpNote({ band, part1: state.part1, part2: state.part2 });



  return (
   <ExplainCtx.Provider value={explain}>
    <div className="min-h-dvh bg-background text-foreground">
      {/* header actions */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-background/70 border-b border-border">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.2em] text-primary">Where you're heading</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="min-h-[40px] px-3 rounded-xl border border-border bg-card hover:bg-secondary
                text-sm inline-flex items-center gap-2"
            >
              <Download className="h-4 w-4" /> Download
            </button>
            <Link
              to="/hub/you#history"
              className="min-h-[40px] px-3 rounded-xl border border-border bg-card hover:bg-secondary
                text-sm inline-flex items-center gap-2
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <History className="h-4 w-4" /> Saved attempts
            </Link>
            <button
              onClick={() => { reset(); navigate("/assessment/v2"); }}
              className="min-h-[40px] px-3 rounded-xl border border-border bg-card hover:bg-secondary
                text-sm inline-flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" /> Start over
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 md:px-8 py-10 md:py-14">
        {/* 1 · Headline */}
        <section>
          <h1 className="font-display text-3xl md:text-5xl leading-tight font-medium">
            {m.headline}
          </h1>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ArchetypeChip name={m.learner.archetypePrimary} tag={m.primaryTag} kind="primary" />
            <ArchetypeChip name={m.learner.archetypeSecondary} tag={m.secondaryTag} kind="secondary" />
          </div>
        </section>

        {/* X-MOBILITY-001 — say plainly how far we assumed you could go */}
        {mobilityResultNote({ band, part1: state.part1, part2: state.part2 }) && (
          <div className="mt-8 rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
            {mobilityResultNote({ band, part1: state.part1, part2: state.part2 })}
          </div>
        )}

        {/* X-CAUSE-001 / X-HOBBY-002 — say plainly what your causes and your
            real habits did to this list, before showing the list. */}
        {lightsNote && (
          <div className="mt-4 rounded-2xl border border-primary/30 bg-primary/5 p-4 text-sm text-foreground/90">
            {lightsNote}
          </div>
        )}

        {/* X-BACKGROUND-001 — what your working history counts for */}
        {(() => {
          const bg = backgroundBlock(
            { band, part1: state.part1, part2: state.part2 },
            m.results[0]?.title ?? null,
          );
          if (!bg) return null;
          return (
            <section className="mt-4 rounded-2xl border border-border bg-card p-5">
              <h2 className="text-sm font-medium text-foreground">Your background</h2>
              <p className="mt-2 text-sm text-muted-foreground">{bg.note}</p>
              {bg.transferables.length > 0 && (
                <>
                  <h3 className="mt-5 text-sm font-medium text-foreground">What transfers</h3>
                  <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
                    {bg.transferables.map((t) => (
                      <li key={t} className="flex gap-2">
                        <span aria-hidden className="text-primary">→</span>
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {bg.nextStep && (
                <p className="mt-5 text-sm text-foreground">
                  <span className="font-medium">Next step. </span>{bg.nextStep}
                </p>
              )}
            </section>
          );
        })()}


        <PendingWritesBanner count={takeover.pendingWrites} />
        <SavedConfirmation count={takeover.pendingWrites} completedAt={takeover.completedAt} />
        <CanonicalReadyBanner takeover={takeover} />
        <PipelineFailureBanner takeover={takeover} />

        {/* study-relaxed banner — only meaningful for the on-device preview */}
        {!takeover.ready && m.studyRelaxed && (
          <div className="mt-8 rounded-2xl border border-sky-500/40 bg-sky-500/5 p-4 text-sm text-sky-300">
            <span className="font-medium">With a short course, these open up too.</span>{" "}
            Your first list was short, so we widened the study limit by one step to show you
            what's within reach.
          </div>
        )}


        {/* Once the sealed report exists it is the authority — the on-device
            preview, its filters and its tiers all step aside. */}
        {takeover.canonical ? (
          <CanonicalCareerList canonical={takeover.canonical} />
        ) : (
          <>
            {familyFilter && (
              <div className="mt-8 flex items-center justify-between rounded-2xl border border-border bg-card px-4 py-3 text-sm">
                <span>Showing more like <span className="font-medium">{familyFilter}</span></span>
                <button onClick={() => setFamilyFilter(null)} className="text-primary hover:underline">
                  Show everything
                </button>
              </div>
            )}

            {/* thin or empty Best-match tier — say why, plainly */}
            {grouped.best.length < 3 && m.learner.salaryFloorLpa > 0 && (
              <div className="mt-8 rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">
                  Your salary floor rules out a lot of hands-on roles.
                </span>{" "}
                Here's what clears it, and here's what's close.
              </div>
            )}

            {/* 2 · Top 3 matches + 3 stretch roles */}
            <div role="group" aria-label="Order your top matches" className="mt-8 inline-flex rounded-2xl border border-border bg-card p-1">
              {([["pay", "Highest pay"], ["fit", "Best fit"]] as const).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={order === k}
                  onClick={() => setOrder(k)}
                  className={`min-h-[44px] px-4 rounded-xl text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    order === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <Tier
              heading="Your top 3 matches"
              subtitle={order === "pay"
                ? "Your good fits that pay the most today. Switch to Best fit to see your closest matches."
                : "Your closest fits today, based on what you told us."}
              results={grouped.top}
              expanded={expanded}
              setExpanded={setExpanded}
              saved={saved}
              toggleSaved={toggleSaved}
              onMoreLikeThis={(family) => setFamilyFilter(family)}
            />
            <Tier
              heading="3 stretch roles"
              subtitle="A step further from where you are today — worth growing toward."
              results={grouped.stretch}
              expanded={expanded}
              setExpanded={setExpanded}
              saved={saved}
              toggleSaved={toggleSaved}
              onMoreLikeThis={(family) => setFamilyFilter(family)}
            />

            {grouped.best.length + grouped.also.length + grouped.explore.length === 0 && (
              <div className="mt-10 rounded-2xl border border-border bg-card p-6 space-y-2">
                <p className="font-display text-xl">Nothing in this filter yet.</p>
                <p className="text-sm text-muted-foreground">
                  That's a filter, not a verdict.{" "}
                  <button onClick={() => setFamilyFilter(null)} className="text-primary hover:underline">
                    Show everything
                  </button>{" "}
                  and your full list comes back.
                </p>
              </div>
            )}
          </>
        )}

        {convergence && !convergence.empty && (
          <section className="mt-14">
            <ConvergenceVenn data={convergence} />
          </section>
        )}

        {/* X-CAUSE-ROLE-001 — Purpose Map: interest → cause → role → directions → experiment. */}
        {purpose.length > 0 && (
          <section className="mt-14" aria-labelledby="purpose-map-title">
            <h2 id="purpose-map-title" className="font-display text-2xl md:text-3xl font-medium">
              Your Purpose Map
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Ways your interests could contribute to what you care about. These are directions to explore today, not a fixed path.
            </p>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              {purpose.map((p) => (
                <article key={p.causeId} className="rounded-3xl border border-border bg-card p-6">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-lg font-medium">{p.cause}</h3>
                    <span className="shrink-0 rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground">
                      Confidence: {p.confidence}
                    </span>
                  </div>
                  <dl className="mt-4 space-y-2 text-sm">
                    {p.interest && (<div><dt className="inline text-muted-foreground">Interest: </dt><dd className="inline">{p.interest}</dd></div>)}
                    {p.role && (<div><dt className="inline text-muted-foreground">Your role: </dt><dd className="inline">{p.role}</dd></div>)}
                  </dl>
                  <p className="mt-3 text-sm leading-relaxed">{p.sentence}</p>
                  {p.experiment && (
                    <p className="mt-3 text-sm text-muted-foreground"><span className="text-foreground font-medium">Try this week: </span>{p.experiment}</p>
                  )}
                  {p.confidence === "Early" && (
                    <p className="mt-3 text-xs text-muted-foreground">Confidence grows as you add interests and try the experiment.</p>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}

        {/* X-CAUSE-001 — nibble-format route in. Hook, cultural anchor, 3–5
            milestones with checkpoints, a 30-minute win for this week. */}
        {causeBlocks.length > 0 && (
          <section className="mt-14">
            <h2 className="font-display text-2xl md:text-3xl font-medium">
              What to do about the things you'd fix
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              In your order. Every route opens with something free, every milestone has a
              checkpoint you can show someone, and every first move fits in half an hour.
            </p>
            <div className="mt-5 space-y-5">
              {causeBlocks.map((c, i) => (
                <CauseBlock key={c.id} c={c} index={i} />
              ))}
            </div>
          </section>
        )}



        {/* 5 · Honest footer */}
        <footer className="mt-16 pt-8 border-t border-border text-sm text-muted-foreground">
          We compared what you told us with all {m.totalConsidered.toLocaleString()} careers we hold.{" "}
          {m.ruledOutByStudy > 0 && (
            <>
              {m.ruledOutByStudy.toLocaleString()} of them ask for more study than you can take on
              right now, so we set those aside — not out.{" "}
            </>
          )}
          Your salary floor, location and qualification were all respected. Where our data is thin,
          we've said so on the card rather than guessing.
          <div className="mt-6">
            <Link
              to="/assessment/v2/dossier"
              className="inline-flex items-center gap-2 min-h-[48px] px-5 rounded-2xl bg-primary
                text-primary-foreground font-medium hover:opacity-90 focus:outline-none
                focus-visible:ring-2 focus-visible:ring-primary"
            >
              Read your full Passport <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="mt-2 text-xs">The long version, with our working shown — printable for home.</p>
          </div>
        </footer>

      </main>
    </div>
   </ExplainCtx.Provider>
  );
}

/**
 * X-CAUSE-001 — one cause, nibble format. Hook first so it gets read at all,
 * then the cultural anchor, then a route with checkpoints, then the smallest
 * possible way to start. Milestones are a real <ol> so screen readers and
 * keyboards get the sequence for free.
 */
function CauseBlock({ c, index }: { c: CauseStepBlock; index: number }) {
  const costTone: Record<string, string> = {
    Free: "border-primary/40 text-primary",
    "Low cost": "border-accent/40 text-accent",
    "Paid route": "border-border text-muted-foreground",
  };
  return (
    <article className="rounded-3xl border border-border bg-card overflow-hidden">
      {/* Hook */}
      <div className="p-5 md:p-6 border-b border-border bg-gradient-to-br from-primary/10 to-transparent">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary
              text-[11px] font-medium text-primary-foreground tabular-nums"
            aria-hidden="true"
          >
            {index + 1}
          </span>
          <span className="text-xs uppercase tracking-[0.16em] text-primary">
            Your {c.ordinal} priority
          </span>
          <span className="text-xs text-muted-foreground">· {c.label}</span>
        </div>
        <p className="font-display text-xl md:text-2xl font-medium leading-snug mt-3">
          {c.hook}
        </p>
      </div>

      <div className="p-5 md:p-6 space-y-5">
        {/* Cultural anchor — the thing they've already seen */}
        <p className="text-sm text-foreground/90 leading-relaxed border-l-2 border-primary/50 pl-4">
          {c.culture}
        </p>

        <p className="text-sm text-muted-foreground leading-relaxed">{c.why}</p>

        {c.matchTitle && (
          <p className="text-sm text-muted-foreground">
            In your list, this shows up as{" "}
            <span className="text-foreground font-medium">{c.matchTitle}</span>.
          </p>
        )}

        {/* Start this week — the 30-minute doorway, placed before the long route
            on purpose. The point is that they can begin today. */}
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs uppercase tracking-[0.2em] text-primary">Start this week</p>
            <span className="text-xs font-medium text-primary tabular-nums">
              {c.quickWin.minutes} min
            </span>
          </div>
          <p className="font-display text-lg font-medium mt-2">{c.quickWin.title}</p>
          <ol className="mt-3 space-y-1.5 text-sm text-foreground/90">
            {c.quickWin.steps.map((step, si) => (
              <li key={step} className="flex gap-2.5">
                <span className="text-primary tabular-nums shrink-0" aria-hidden="true">
                  {si + 1}.
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          <p className="mt-3 text-xs text-muted-foreground">
            <span className="text-foreground font-medium">You'll have:</span> {c.quickWin.proof}
          </p>
        </div>

        {/* The route in */}
        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Then the route in · {c.milestones.length} milestones
          </h4>
          <ol className="mt-3 space-y-3">
            {c.milestones.map((m, mi) => (
              <li key={m.title} className="relative rounded-2xl border border-border bg-background/40 p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-baseline gap-2.5 min-w-0">
                    <span
                      className="text-xs text-primary tabular-nums shrink-0"
                      aria-hidden="true"
                    >
                      {mi + 1}
                    </span>
                    <h5 className="font-display text-base md:text-lg font-medium leading-snug">
                      {m.title}
                    </h5>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-muted-foreground">{m.window}</span>
                    <span
                      className={[
                        "text-[11px] px-2 py-0.5 rounded-full border",
                        costTone[m.cost] ?? "border-border text-muted-foreground",
                      ].join(" ")}
                    >
                      {m.cost}
                    </span>
                  </div>
                </div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{m.detail}</p>
                <p className="mt-2 text-sm">
                  <span className="text-xs uppercase tracking-[0.16em] text-primary">
                    Checkpoint
                  </span>
                  <span className="block mt-1 text-foreground/90">{m.checkpoint}</span>
                </p>
              </li>
            ))}
          </ol>
        </div>

        {/* The bigger month-scale move */}
        <div className="rounded-2xl border border-border p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            And this month
          </p>
          <p className="mt-2 text-sm text-foreground">{c.firstAction}</p>
        </div>
      </div>
    </article>
  );
}

/**
 * X-CAUSE-001 / X-HOBBY-002 explainability. Held in context so every card in
 * every tier explains itself in the learner's own words without threading a
 * prop through four components.
 */
const EMPTY_EXPLAIN: MatchExplainContext = { causes: new Map(), hobbies: new Map(), observed: new Map() };
const ExplainCtx = createContext<MatchExplainContext>(EMPTY_EXPLAIN);

function ArchetypeChip({ name, tag, kind }: { name: string; tag: string; kind: "primary" | "secondary" }) {
  return (
    <div className={[
      "rounded-2xl border p-4",
      kind === "primary" ? "border-primary/50 bg-primary/10" : "border-border bg-card",
    ].join(" ")}>
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
        {kind === "primary" ? "Mostly" : "And also"}
      </div>
      <div className="font-display text-xl md:text-2xl font-medium mt-1">{name}</div>
      <div className="text-sm text-muted-foreground mt-1">{tag}.</div>
    </div>
  );
}

function Tier({
  heading, subtitle, results, expanded, setExpanded, saved, toggleSaved, onMoreLikeThis,
}: {
  heading: string; subtitle: string; results: MatchResult[];
  expanded: string | null; setExpanded: (id: string | null) => void;
  saved: Set<string>; toggleSaved: (id: string) => void;
  onMoreLikeThis: (family: string) => void;
}) {
  if (results.length === 0) return null;
  return (
    <section className="mt-12">
      <h2 className="font-display text-2xl md:text-3xl font-medium">{heading}</h2>
      <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
      <div className="mt-4 space-y-3">
        {results.map((r) => (
          <CareerCard
            key={r.ciId}
            r={r}
            expanded={expanded === r.ciId}
            onToggle={() => setExpanded(expanded === r.ciId ? null : r.ciId)}
            saved={saved.has(r.ciId)}
            onSave={() => toggleSaved(r.ciId)}
            onMoreLikeThis={() => onMoreLikeThis(r.family)}
          />
        ))}
      </div>
    </section>
  );
}

function CareerCard({
  r, expanded, onToggle, saved, onSave, onMoreLikeThis,
}: {
  r: MatchResult; expanded: boolean; onToggle: () => void;
  saved: boolean; onSave: () => void; onMoreLikeThis: () => void;
}) {
  const why = whyItMatched(r, useContext(ExplainCtx));
  const distanceClass = DISTANCE_STYLES[r.distance] ?? "bg-secondary text-muted-foreground border-border";
  const dim = r.confidence === "low" || r.market === "unpriced";

  return (
    <motion.div layout className="rounded-3xl border border-border bg-card">
      <button
        onClick={onToggle}
        className="w-full text-left p-5 md:p-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-3xl"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${distanceClass}`}>
                {r.distance}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">{formatSalary(r.salaryLo, r.salaryHi)}</span>
              {r.passion > 0 && (
                <span className="text-xs font-medium px-2.5 py-1 rounded-full border
                  border-primary/40 bg-primary/10 text-primary">
                  Drawn to this
                </span>
              )}
              {dim && (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <Info className="h-3 w-3" /> We know less about this one
                </span>
              )}
            </div>
            <div className="mt-2 font-display text-lg md:text-xl font-medium leading-snug">
              {r.title}
            </div>
            <div className="text-sm text-muted-foreground">{r.family}</div>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onSave(); }}
              aria-label={saved ? "Unsave" : "Save"}
              className={[
                "h-9 w-9 grid place-items-center rounded-full border",
                saved ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              <Star className="h-4 w-4" fill={saved ? "currentColor" : "none"} />
            </button>
            {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 md:px-6 pb-6 space-y-4">
              {why.fits.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-2">Why it matched</div>
                  <p className="text-sm text-foreground/90 leading-relaxed">
                    {why.fits.join(". ")}.
                  </p>
                </div>
              )}
              {why.watch && (
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-2">Worth knowing</div>
                  <p className="text-sm text-foreground/90 leading-relaxed">{why.watch}.</p>
                </div>
              )}
              <div className="pt-2 flex flex-wrap items-center gap-3">
                <Link
                  to={`/careers/${r.ciId}`}
                  state={{ from: "/assessment/v2/result" }}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5
                    rounded-2xl bg-primary text-primary-foreground font-medium hover:opacity-90
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  Explore this career <ArrowRight className="h-4 w-4" />
                </Link>
                <button
                  onClick={(e) => { e.stopPropagation(); onMoreLikeThis(); }}
                  className="inline-flex items-center gap-1 min-h-[44px] px-3 text-sm text-primary hover:underline"
                >
                  Show me more like this <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function BandedResult() {
  const { band } = useAssessmentV2();
  recordRouteHit("AssessmentV2Result", `band=${band}`);
  // 12–14 gets worlds, never job titles. Every other band gets the ranked
  // career list. Same state, same engine — a different output model.
  return band === "12to14" ? <ExplorerResult /> : <ResultInner />;
}

export default function AssessmentV2Result() {
  const [params] = useSearchParams();
  const band = parseBand(params.get("band"));
  return (
    <AssessmentV2Provider key={band} band={band}>
      <BandedResult />
    </AssessmentV2Provider>
  );
}
